<template>
  <!-- No contact details chosen (or none added yet): the public site leaves the panel out, so the contact form stands
       alone; the studio's preview shows where they'll go. -->
  <SectionContainer v-if="hasAny || inStudio"
      :backgroundVariant="backgroundVariant"
      :colors="colors" class="w-full flex flex-col items-center justify-center"
  >
    <div :style="cssVars" class=" w-full flex items-center justify-center px-10  flex-col overflow-x-scroll sm:overflow-x-auto ">
      <component :is="headingTag" class="text-3xl font-bold text-center text-gray-900 dark2:text-white">
        {{ title }}
      </component>
      <p class="text-lg text-gray-700 dark2:text-gray-300 text-center mt-2">
        {{ description }}
      </p>

      <p v-if="!hasAny" class="mt-6 text-sm text-gray-600 text-center">Your phone, email and address show here once you add them in My Business.</p>
      <!-- Contact Details -->
      <div v-if="hasAny" class="mt-6 space-y-4 text-start flex flex-col gap-x-4">
        <div v-if="phone" v-for="p in phone.filter(p => includedIds.includes(p.id))" class="flex flex-row gap-x-4">
          <div class="font-semibold w-32 text-gray-900 dark2:text-white flex flex-row gap-x-2">
            <Phone></Phone>
            <span>{{ p.label }}</span></div>
          <a :href="`tel:${String(p.number || '').replace(/[^\d+]/g, '')}`" class="text-blue-800 text-nowrap hover:underline">{{ p.number }}</a>
        </div>
        <div v-if="email" class="flex flex-row gap-x-4" v-for="e in email.filter(e => includedIds.includes(e.id))">
          <div class="font-semibold w-32 text-gray-900 dark2:text-white flex flex-row gap-x-2">
            <Mail></Mail>
            <span>{{ e.label }}</span></div>
          <a :href="`mailto:${email}`" class="text-blue-800 hover:underline">{{ e.address }}</a>
        </div>
        <CookieToggle v-if="hasMappable" class="mt-4 text-xs"
                      v-model="thirdPartyEnabled"
                      title="Enable Google Maps"
                      sub-text="To enable the map you must accept any third party cookies the map provider may use."
                      selected-text="Enabled"
                      unselected-text="Disabled"
                      storage-key="draksy.thirdPartyContent"
                      :persist="true"
                      :default-value="false"
                      :is-editing="true"
        />

        <div v-if="address.length > 0" v-for="a in address.filter(ad => includedIds.includes(ad.id))"
             class="flex flex-col gap-x-4">
          <div class="flex flex-col w-full gap-x-4">
            <span
                class="font-semibold text-gray-900 dark2:text-white w-32">{{ a.label !== '' ? a.label : 'Address:' }} </span>
            <p class="text-gray-700 dark2:text-gray-300">{{
                [a.suite, (a.houseNumber || '') + ' ' + a.street, a.city, a.state, a.postcode].filter((f) => f && f !== '').join(', ')
              }}</p>
          </div>
          <div v-if="a  && thirdPartyEnabled" class="mt-8 w-full h-64 rounded-lg overflow-hidden shadow-md">
            <iframe v-if="a.longitude && a.latitude"
                    ref="mapIframe"
                    :src="`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_EMBED_API_KEY}&q=${a.latitude},${a.longitude}&zoom=11`"
                    width="100%"
                    height="400"
                    frameborder="0"
                    style="border:0"
                    allowfullscreen=""
                    :key="`${a.latitude}-${a.longitude}`"
            ></iframe>
          </div>
        </div>
      </div>
      <div class="w-full h-20">

      </div>
    </div>
  </SectionContainer>
</template>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import {computed, ref} from "vue";
import SectionContainer from "@/components/SectionContainer.vue";
import {Mail, Phone} from '@iconoir/vue';
import CookieToggle from "@/components/CookieToggle.vue";
import {studioMode} from "@/classes/previewBridge.js";
const headingTag = useSectionHeadingTag();

const props = defineProps({
  title: {type: String, default: "Contact Information"},
  description: {type: String, default: "Reach out to us with any questions or inquiries."},
  phone: {type: Array, default: () => []},
  email: {type: Array, default: () => []},
  address: {type: Array, default: ""},
  colors: {type: Object, required: true},
  backgroundVariant: {type: Number, default: 1},
  includedIds: {type: Array, default: () => []},
});

const ids = computed(() => (Array.isArray(props.includedIds) ? props.includedIds : []));
const hasAny = computed(() => [props.phone, props.email, props.address].some((list) => Array.isArray(list) && list.some((x) => ids.value.includes(x?.id))));
const inStudio = computed(() => studioMode.value !== 'off');
// The map switch only when a shown address has a location to map.
const hasMappable = computed(() => Array.isArray(props.address) && props.address.some((a) => ids.value.includes(a?.id) && a?.latitude && a?.longitude));

const GOOGLE_MAPS_EMBED_API_KEY = 'AIzaSyD6FRpaB49AJPJUBoQIoSKVCJ1JeJGtGD8';  // Replace with your API key
const thirdPartyEnabled = ref(false);

// Dynamic colors for styling
const cssVars = computed(() => ({
  "--accent": "var(--brand-accent)"
}));
</script>

<style scoped>
/* Responsive styles */
iframe {
  border-radius: 8px;
}
</style>
